import { navigate } from "./app.js";
import { getCurrentUser } from "./auth.js";

/**
 * The navbar search control.
 *
 * Collapsed to an icon until used, then a type selector plus a text
 * field — the same shape as Strava's. Submitting goes to /search, so a
 * result set has its own URL and can be linked, reloaded and gone back
 * to, rather than living in a dropdown that vanishes.
 */

/** The four things worth searching, and where each one looks.
 *
 * Keyed by the value that appears in the URL, so a link stays readable. */
export const SEARCH_TYPES = {
  divers: { label: "Divers", path: "/users" },
  cohorts: { label: "Cohorts", path: "/cohorts" },
  "dive-sites": { label: "Dive sites", path: "/dive-sites" },
  "dive-events": { label: "Dive events", path: "/dive-plans" },
};

// Insertion order drives the dropdown and the result pills, so this
// object is the single place the order is decided. Keep it in step with
// the <select> in index.html, which is static markup.
//
// The default is the first entry: a fresh <select> shows its first
// option, so anything else would mean the navbar displayed one type
// while a hand-edited URL fell back to another.
export const DEFAULT_SEARCH_TYPE = "divers";

/** Normalises a type from a URL, so a hand-edited link cannot pick a
 *  type that does not exist. */
export function resolveType(value) {
  return Object.hasOwn(SEARCH_TYPES, value ?? "") ? value : DEFAULT_SEARCH_TYPE;
}

let wrap = null;
let form = null;
let fields = null;
let toggle = null;
let input = null;
let typeSelect = null;

function expanded() {
  return form != null && !form.classList.contains("d-none");
}

function setExpanded(open) {
  if (!form || !toggle) return;
  form.classList.toggle("d-none", !open);
  // The form is a flex row when shown; d-none wins while collapsed.
  form.classList.toggle("d-flex", open);
  toggle.setAttribute("aria-expanded", String(open));
  if (open) input?.focus();
}

/** Hides the whole control for signed-out readers: /search is a
 *  protected route, so offering it would only bounce them to login. */
export function refreshSearchVisibility() {
  if (!wrap) return;
  const signedIn = getCurrentUser() != null;
  wrap.classList.toggle("d-none", !signedIn);
  wrap.classList.toggle("d-flex", signedIn);
  if (!signedIn) setExpanded(false);
}

/** Puts the control back in step with the URL, so re-opening it shows the
 *  query that produced the page.
 *
 *  Deliberately does not expand. The expanded field takes width from the
 *  nav links — measured: fine from 1200px, but their labels wrap to two
 *  lines between 992 and 1199 — so it stays a transient state you opt
 *  into. The results page carries its own field for refining a search. */
export function syncSearchControl({ type, q } = {}) {
  if (!wrap) return;
  if (typeSelect && type) typeSelect.value = resolveType(type);
  if (input && q != null) input.value = q;
}

export function initSearchControl() {
  wrap = document.getElementById("nav-search-wrap");
  form = document.getElementById("nav-search");
  if (!wrap || !form) return;
  fields = document.getElementById("nav-search-fields");
  toggle = document.getElementById("nav-search-toggle");
  input = document.getElementById("nav-search-input");
  typeSelect = document.getElementById("nav-search-type");

  // Idempotent: boot() calls this once, but a second call — from a test,
  // or from any future re-init — would otherwise stack a second handler
  // and every click would toggle twice, netting no change at all.
  if (toggle.dataset.searchBound === "true") {
    refreshSearchVisibility();
    return;
  }
  toggle.dataset.searchBound = "true";

  toggle.addEventListener("click", () => setExpanded(!expanded()));

  // Escape collapses rather than clearing, so a stray key does not lose
  // what was typed.
  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      setExpanded(false);
      toggle.focus();
    }
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) {
      // Nothing to search for; keep the field open rather than navigating
      // to an empty result set.
      input.focus();
      return;
    }
    const params = new URLSearchParams({ type: resolveType(typeSelect.value), q });
    // Collapse on the way out: the results page shows the query and has
    // its own field, so leaving this open would only squeeze the nav.
    setExpanded(false);
    navigate(`/search?${params.toString()}`);
  });

  refreshSearchVisibility();
}
